import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, Map } from 'lucide-react';
import { toast } from 'sonner';

const SitemapGeneratorTool = () => {
  const [urls, setUrls] = useState('');
  const [changefreq, setChangefreq] = useState('weekly');
  const [priority, setPriority] = useState('0.8');

  const generateSitemap = () => {
    const urlList = urls.split('\n').filter(u => u.trim());
    if (urlList.length === 0) return '';

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

    urlList.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${url.trim()}</loc>\n`;
      xml += `    <changefreq>${changefreq}</changefreq>\n`;
      xml += `    <priority>${priority}</priority>\n`;
      xml += '  </url>\n';
    });

    xml += '</urlset>';
    return xml;
  };

  const output = generateSitemap();

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success('Sitemap XML copied!');
  };

  const downloadFile = () => {
    const blob = new Blob([output], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded sitemap.xml');
  };

  return (
    <div className="space-y-6" data-testid="sitemap-generator-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              URLs (one per line)
            </label>
            <Textarea
              placeholder="https://example.com/&#10;https://example.com/about&#10;https://example.com/contact"
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Change Frequency</label>
              <select
                value={changefreq}
                onChange={(e) => setChangefreq(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-muted border border-border"
              >
                <option value="always">Always</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="never">Never</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-muted border border-border"
              >
                <option value="1.0">1.0 (Highest)</option>
                <option value="0.9">0.9</option>
                <option value="0.8">0.8</option>
                <option value="0.7">0.7</option>
                <option value="0.6">0.6</option>
                <option value="0.5">0.5 (Default)</option>
                <option value="0.4">0.4</option>
                <option value="0.3">0.3</option>
                <option value="0.2">0.2</option>
                <option value="0.1">0.1 (Lowest)</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <Map className="w-4 h-4 text-primary" />
            Generated sitemap.xml
          </label>
          <Textarea
            value={output}
            readOnly
            className="min-h-[300px] font-mono text-sm bg-muted/50"
            placeholder="Sitemap XML will appear here..."
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyOutput} disabled={!output}>
          <Copy className="w-4 h-4 mr-2" /> Copy XML
        </Button>
        <Button onClick={downloadFile} variant="outline" disabled={!output}>
          Download sitemap.xml
        </Button>
        <Button onClick={() => setUrls('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default SitemapGeneratorTool;
